import './alteration-area.scss'

import _ from 'lodash'
import Component from "vue-class-component"
import { Watch } from 'vue-property-decorator'
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons'

import { getTranslatedString, Alteration, ThesaurusTerm } from '@skinsoft/types'
import { ThesaurusTermDataStore } from '@skinsoft/entity'
import ConditionReportMediasArea from '../condition-report-medias-area'
import { ReactiveVue } from "@skinsoft/reactive-vue"
import { DELETE_IMAGE_CLICK_TOPIC, MEDIA_ID_CHANGED_FROM_CACHE, PRINCIPAL_IMAGE_CLICK_TOPIC } from '@skinsoft/bus'
import AlterationList from './alteration-list'
import { ALTERATION_EDITED, DELETE_ALTERATION } from '../drawing-editor-components/alteration-line'
import CreateAlterationForm, { ALTERATION_CREATED } from './create-alteration-form'
import AlterationCardList from './alteration-card-list'
import { ALTERATION_CARD_ALTERATION_UPDATED, ALTERATION_CARD_DELETE_ALTERATION, AlterationWithThesaurusTerm } from './alteration-card'
import AlterationCardGroup, { GroupingAlterationField } from './alteration-card-group'

const MEDIA_VIEW = 'media-vue'
const ALTERATION_TYPE_VIEW = 'alteration-type-view'
const ALTERATION_TITLE_VIEW = 'alteration-title-view'

@Component({
    components: {
        sConditionReportMediasArea: ConditionReportMediasArea,
        sAlterationList: AlterationList,
        sCreateAlterationForm: CreateAlterationForm,
        sAlterationCardList: AlterationCardList,
        sAlterationCardGroup: AlterationCardGroup,
    },
    props: ['form', 'fieldName', 'associatedMedias', 'alterations'],
    template: require('./alteration-area.html')
})
export default class AlterationArea extends ReactiveVue {

    private menuBurgerIcon = faBars
    private closeIcon = faTimes

    private alterations: Alteration[]
    
    private alterationsByAlterationType: { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] } = {}
    private alterationsByTitle: { [title: string]: (AlterationWithThesaurusTerm & { index?: number })[] } = {}

    private sections: string[] = [MEDIA_VIEW, ALTERATION_TYPE_VIEW, ALTERATION_TITLE_VIEW]
    private activeSection: number = 0

    private isViewMenuOpen = false

    private $thesaurusTermDataStore: ThesaurusTermDataStore

    private GROUP_BY_ALTERATION_TYPE = GroupingAlterationField.AlterationType
    private GROUP_BY_TITLE = GroupingAlterationField.Title

    // section names
    private MEDIA_VIEW = MEDIA_VIEW
    private ALTERATION_TYPE_VIEW = ALTERATION_TYPE_VIEW
    private ALTERATION_TITLE_VIEW = ALTERATION_TITLE_VIEW

    // events
    private DELETE_IMAGE_CLICK_TOPIC = DELETE_IMAGE_CLICK_TOPIC
    private MEDIA_ID_CHANGED_FROM_CACHE = MEDIA_ID_CHANGED_FROM_CACHE
    private PRINCIPAL_IMAGE_CLICK_TOPIC = PRINCIPAL_IMAGE_CLICK_TOPIC
    private ALTERATION_EDITED = ALTERATION_EDITED
    private GLOBAL_ALTERATION_CREATED = ALTERATION_CREATED
    private DELETE_ALTERATION = DELETE_ALTERATION
    private ALTERATION_CARD_ALTERATION_UPDATED = ALTERATION_CARD_ALTERATION_UPDATED
    private ALTERATION_CARD_DELETE_ALTERATION = ALTERATION_CARD_DELETE_ALTERATION

    private onDeleteAlteration = this.getListener(DELETE_ALTERATION)
    private onAddMedia = this.getListener('addMedia')
    private onDeleteMedia = this.getListener(DELETE_IMAGE_CLICK_TOPIC)
    private onChangeFirstMedia = this.getListener(PRINCIPAL_IMAGE_CLICK_TOPIC)
    private onMediaIdChangeFromCache = this.getListener(MEDIA_ID_CHANGED_FROM_CACHE)
    private onAlterationChanged = this.getListener(ALTERATION_EDITED)
    private onAlterationCreated = this.getListener(ALTERATION_CREATED)
    private onAlterationChangedCard = this.getListener(ALTERATION_CARD_ALTERATION_UPDATED)
    private onDeleteAlterationCard = this.getListener(ALTERATION_CARD_DELETE_ALTERATION)

    private getListener(key: string): Function {
        const eventListener: Function = this.$listeners[key] as Function
        if(eventListener) {
            return eventListener
        }

        return () => {}
    }

    private mounted() {
        this.sortAlterations()
    }

    private sortAlterations() {
        if(this.alterations) {
            const allAlterationTypes: string[] = _.uniq(this.alterations.map(alteration => alteration.typeOfAlteration))
            this.$thesaurusTermDataStore.fetchMany2(allAlterationTypes, this.$i18n.locale)
                .subscribe((response) => {
                    let thesaurusTermsArray: ThesaurusTerm[] = []
                    if(response) {
                        if(Array.isArray(response)) {
                            thesaurusTermsArray = response
                        }
                        else if(response.data && Array.isArray(response.data)){
                            thesaurusTermsArray = response.data
                        }
                    }

                    this.alterationsByAlterationType = this.getAlterationsByAlterationTypes(allAlterationTypes, thesaurusTermsArray)
                    this.alterationsByTitle = this.getAlterationsByTitle(thesaurusTermsArray)
                })
        } else {
            this.alterationsByAlterationType = {}
            this.alterationsByTitle = {}
        }
    }

    private getAlterationsByAlterationTypes(allAlterationTypeIds: string[], alterationTypeThesaurusTerms: ThesaurusTerm[]): 
        { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] } {
            
        let result: { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] } = {}

        if(this.alterations) {
        
            const alterationsWithIndex: (Alteration & { index?: number })[] = this.alterations.map((alteration, index) => {
                return {...alteration, index}
            })

            for(const alterationTypeId of allAlterationTypeIds) {
                const alterationTypeThesaurusTerm = alterationTypeThesaurusTerms?.find(thesaurusTerm => thesaurusTerm.id === alterationTypeId)
                result[alterationTypeId] = alterationsWithIndex
                                            .filter(alteration => alteration.typeOfAlteration === alterationTypeId)
                                            .map(alteration => {
                                                return {
                                                    ...alteration, 
                                                    typeOfAlterationObject: alterationTypeThesaurusTerm ?? null
                                                } as AlterationWithThesaurusTerm & { index?: number }
                                            })
            }
        }

        return result
    }

    private getAlterationsByTitle(alterationTypeThesaurusTerms: ThesaurusTerm[]): { [title: string]: (AlterationWithThesaurusTerm & { index?: number })[] } {
        let result: { [alterationTypeId: string]: (AlterationWithThesaurusTerm & { index?: number })[] } = {}

        if(this.alterations) {
            const locale = this.$i18n.locale
            const allTitles = _.uniq(this.alterations.map(alteration => getTranslatedString(alteration.title, locale)))

            const alterationsWithIndex: (AlterationWithThesaurusTerm & { index?: number })[] = this.alterations.map((alteration, index) => {
                return {
                    ...alteration, 
                    index,
                    typeOfAlterationObject: alterationTypeThesaurusTerms.find(thesaurusTerm => thesaurusTerm.id === alteration.typeOfAlteration) ?? null,
                }
            })
    
            for(const title of allTitles) {
                result[title] = alterationsWithIndex.filter(alteration => getTranslatedString(alteration.title, locale) === title)
            }
        }

        return result
    }

    private isActiveSection(sectionName: string) {
        return this.sections[this.activeSection] === sectionName
    }

    private changeSection(sectionName: string) {
        const sectionIndex = this.sections.indexOf(sectionName)
        if(sectionIndex >= 0) {
            this.activeSection = sectionIndex
        }
        this.isViewMenuOpen = false
    }

    private toggleViewMenu() {
        this.isViewMenuOpen = !this.isViewMenuOpen
    }

    @Watch('alterations', { deep: true })
    private onAlterationsChanged() {
        this.sortAlterations()
    }
}